import {DefaultCart} from '../../reducers/cartReducer';
import {formatCurrencyString} from '../../util/currency';
import {getLocaleText} from '../../util/translation';
import {$qsAll} from '../../../../@shared/ts/dom';
import {type LoadingMode, type ModalPage} from '../../models/IEnvironment';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {type SquareGateway} from '../../models/square';
import {Environment} from '../../reducers/environmentReducer';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';

export function setupSquareButtonRendering(gateways: Record<string, SquareGateway>) {
	store.subscribe(() => {
		renderSquareButtonDisplay(
			PaymentConfiguration.selectedGateway(),
			Environment.modalUI.page(),
			Environment.modalUI.loadingMode(),
			PaymentConfiguration.gatewayConfig(PaymentConfiguration.selectedGateway()),
		);

		renderSquareButtonLoading(
			Environment.modalUI.loadingMode(),
			PaymentConfiguration.gatewayConfig(PaymentConfiguration.selectedGateway()),
		);

		controlSquareCustomButtonMounts(gateways);
	});
}

function controlSquareCustomButtonMounts(gateways: Record<string, SquareGateway>) {
	if (!PaymentConfiguration.selectedGateway().startsWith('peachpay_square_')) {
		return;
	}

	// Sorting the gateways makes sure the last active gateway is always detached before the new gateway is
	// attached because they share containers.
	for (const [_, gateway] of Object.entries(gateways).sort(([_, m1], [__, m2]) => (m2.state?.mountTarget?.length ?? 0) - (m1.state?.mountTarget.length ?? 0))) {
		if (!gateway.state) {
			gateway.state = {
				mountTarget: '',
			};
		}

		const newMountTarget = customButtonMountTarget();

		// Unmount custom button if already mounted and if the mount target has changed
		if (gateway.state.mountTarget && gateway.state.mountTarget !== newMountTarget) {
			gateway?.detach?.();
			gateway.state.mountTarget = '';
		}

		if (PaymentConfiguration.selectedGateway() === gateway.config.gatewayId) {
			if (gateway.state.mountTarget) {
				continue;
			}

			gateway?.attach?.(newMountTarget);

			gateway.state.mountTarget = newMountTarget;
		} else {
			if (!gateway.state.mountTarget) {
				continue;
			}

			gateway?.detach?.();

			gateway.state.mountTarget = '';
		}
	}
}

/**
 * Renders the square button display state.
 */
function renderSquareButtonDisplay(gatewayId: string, page: ModalPage, loadingMode: LoadingMode, config: GatewayConfiguration | null) {
	// show/hide Square button containers
	if (gatewayId.startsWith('peachpay_square_') && page === 'payment') {
		$qsAll('.square-btn-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.square-btn-container', $element => {
			$element.classList.add('hide');
		});
	}

	// Show/Hide the custom square button container
	if (loadingMode === 'finished' && config?.hasCustomButton) {
		$qsAll('.square-custom-btn-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.square-custom-btn-container', $element => {
			$element.classList.add('hide');
		});
	}

	// Hide/show the non custom square buttons
	if (loadingMode === 'loading' || config?.hasCustomButton) {
		$qsAll('.square-btn', $element => {
			$element.classList.add('hide');
		});
	} else {
		$qsAll('.square-btn', $element => {
			$element.classList.remove('hide');
		});
	}
}

/**
 * Renders the square loading spinners elements based on store state.
 */
function renderSquareButtonLoading(mode: LoadingMode, config: GatewayConfiguration | null) {
	// Show/hide the external spinner
	if (mode === 'loading' || (mode === 'processing' && config?.hasCustomButton)) {
		$qsAll('.square-spinner-container', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.square-spinner-container', $element => {
			$element.classList.add('hide');
		});
	}

	// Show/hide the internal spinner
	if (mode === 'processing') {
		$qsAll('.square-btn-spinner', $element => {
			$element.classList.remove('hide');
		});
	} else {
		$qsAll('.square-btn-spinner', $element => {
			$element.classList.add('hide');
		});
	}

	// Show/hide processing message
	if (mode === 'processing') {
		$qsAll('.square-btn > .button-text', $element => {
			$element.innerHTML = getLocaleText('Processing');
		});
	} else {
		$qsAll('.square-btn > .button-text', $element => {
			$element.innerHTML = `${getLocaleText('Pay')} ${formatCurrencyString(DefaultCart.total())}`;
		});
	}

	// Enable/disable the square non custom buttons while loading
	if (mode === 'finished') {
		$qsAll<HTMLInputElement>('.square-btn', $element => {
			$element.disabled = false;
		});
	} else {
		$qsAll<HTMLInputElement>('.square-btn', $element => {
			$element.disabled = true;
		});
	}
}

function customButtonMountTarget(): string {
	if (Environment.customer.mobile()) {
		return '#mobile-customer-pay-button .square-custom-btn-container';
	}

	return '#new-customer-pay-button .square-custom-btn-container';
}
